import * as geom from "js/core/utilities/geom";
import { TextGroup } from "js/elements/base/TextGroup";
import { ContentElement } from "js/elements/base/ContentElement";
import { CollectionElement, CollectionItemElement } from "js/elements/base/CollectionElement";
import { AssetType, BackgroundStyleType, TextBlockPreset } from "common/constants";
import { BoxFrame, ImageFrame } from "js/elements/base/Frame";
import { TextElement } from "js/elements/base/TextElement";

class PhotoTextList extends CollectionElement {
    getChildItemType() {
        return PhotoTextListItem;
    }

    get showGutter() {
        return true;
    }

    get frame() {
        return this.model.frame || "none";
    }

    get showShadow() {
        return this.frame == "light";
    }

    get aspectRatio() {
        return this.model.aspectRatio || "fill";
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let layouter = this.getLayouter(this.itemElements, size);
        layouter.calcHorizontalLayout({ verticallyAlignText: true });
        layouter.alignHorizontally();

        let isFit = layouter.isFit && this.itemCount <= (this.styles.maxItemCount || 4);

        return { size: layouter.size, isFit };
    }
}

class PhotoTextListItem extends CollectionItemElement {
    get showFrame() {
        return this.frameStyle != "none";
    }

    get frameStyle() {
        return this.parentElement.model.frame || "none";
    }

    get showShadow() {
        return this.parentElement.model.showShadow;
    }

    get aspectRatio() {
        return this.parentElement.aspectRatio;
    }

    get selectionPadding() {
        return { top: 0, left: 0, right: 0, bottom: 10 };
    }

    _build() {
        if (this.showFrame) {
            if (this.frameStyle.equalsAnyOf("light", "dark", "color")) {
                this.frame = this.addElement("frame", () => BoxFrame);
            } else {
                this.frame = this.addElement("frame", () => ImageFrame, {
                    model: {
                        frame: this.frameStyle
                    }
                });
            }
        }
        this.content = this.addElement("contentElement", () => ContentElement, {
            defaultAssetType: AssetType.IMAGE
        });

        this.text = this.addElement("textGroup", () => TextGroup, {
            model: this.model,
            title: {
                singleLine: true,
                allowAlignment: true,
            },
            body: {
                allowEmptyLines: false,
                allowAlignment: true,
            }
        });
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let textProps = children.setProps(this.text, new geom.Size(size.width, options.textHeight));
        textProps.bounds = new geom.Rect(0, size.height - options.textHeight, textProps.size);

        let contentBounds = new geom.Rect(0, 0, size.width, size.height - options.textHeight);

        if (this.aspectRatio == "fit") {
            let contentAspectRatio = this.content.getContentSize().aspectRatio;

            let contentWidth = contentBounds.width * contentAspectRatio;
            let contentHeight = contentWidth / contentAspectRatio;
            if (contentWidth > contentBounds.width) {
                contentWidth = contentBounds.width;
                contentHeight = contentWidth / contentAspectRatio;
            }
            if (contentHeight > contentBounds.height) {
                contentHeight = contentBounds.height;
                contentWidth = contentHeight * contentAspectRatio;
            }

            contentBounds = new geom.Rect(0, 0, contentWidth, contentHeight);
            contentBounds.left = size.width / 2 - contentBounds.width / 2;
            contentBounds.top = (size.height - options.textHeight) / 2 - contentBounds.height / 2;
        }

        if (this.showFrame) {
            switch (this.frameStyle) {
                case "light":
                    this.frame.styles.frameColor = "white";
                    this.frame.styles.shadow = {
                        blur: 14,
                        offsetX: 0,
                        offsetY: 0,
                        opacity: 0.2
                    };
                    break;
                case "dark":
                    this.frame.styles.frameColor = "black";
                    this.frame.showShadow = false;
                    break;
                case "color":
                    this.frame.styles.frameColor = "slide";
                    this.frame.showShadow = false;
                    break;
            }

            let frameProps = children.setProps(this.frame, contentBounds.size);
            frameProps.bounds = contentBounds;
            contentBounds = contentBounds.deflate(frameProps.strokeWidth);
        }

        let contentProps = children.setProps(this.content, contentBounds.size);
        contentProps.bounds = contentBounds;

        return { size };
    }

    getBackgroundColor(forElement) {
        if (forElement) {
            return this.getParentBackgroundColor(forElement);
        } else {
            return BackgroundStyleType.IMAGE;  // this is for the inner widgets in the selection overlay
        }
    }
}

export const elements = {
    PhotoTextList
};
